import JumboCardQuick from '@jumbo/components/JumboCardQuick';
import { List, PictureAsPdf, TableChart } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionIcon from '@mui/icons-material/Description';
import {
    Button,
    ButtonGroup,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { getDecisionsReportDataApi, generateDecisionsReportEXCELApi, generateDecisionsReportPDFApi, generateDecisionsReportWORDApi } from 'api/report';
import LoadingPopUp from 'app/components/LoadingPopup';
import { getDateStringYYYYMMDD, getMergedAuthorizedInsts, getMergedAuthorizedUnits } from 'app/utils/appHelpers';
import config from 'config';
import 'dayjs/locale/tr';
import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import DecisionsReportTable from './DecisionsReportTable';

function ReportDecisionsReportPage() {
    const allAuthorizedInsts = useSelector(state => state.auth.allAuthorizedInsts);
    const allReadAuthorizedInsts = useSelector(state => state.auth.allReadAuthorizedInsts);
    const allAuthorizedUnits = useSelector(state => state.auth.allAuthorizedUnits);
    const allReadAuthorizedUnits = useSelector(state => state.auth.allReadAuthorizedUnits);
    const [state, setState] = useState({
        inst: null,
        unitId: null,
        department: [],
        startedDate: null,
        finishedDate: null,
        meetingNo: ''
    });

    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [lastFilter, setLastFilter] = useState(null);
    const [dataInstOptions, setDataInstOptions] = useState([]);
    const [dataUnitOptions, setDataUnitOptions] = useState([]);
    const [dataDepartmentOptions, setDataDepartmentOptions] = useState([]);
    const [fixedUnit, setFixedUnit] = useState(null);
    const [mergedAuthorizedInsts, setMergedAuthorizedInsts] = useState([]);
    const [mergedAuthorizedUnits, setMergedAuthorizedUnits] = useState([]);
    const isInstLocked = mergedAuthorizedInsts.length === 1;

    const isFormValid = () => {
        return state.inst;
    };

    useEffect(() => {
        setMergedAuthorizedInsts(getMergedAuthorizedInsts(allAuthorizedInsts, allReadAuthorizedInsts));
        setMergedAuthorizedUnits(getMergedAuthorizedUnits(allAuthorizedUnits, allReadAuthorizedUnits));
    }, [allAuthorizedInsts, allReadAuthorizedInsts, allAuthorizedUnits, allReadAuthorizedUnits]);

    useEffect(() => {
        if (mergedAuthorizedInsts && mergedAuthorizedInsts.length > 0) {
            setDataInstOptions(mergedAuthorizedInsts);
            if (mergedAuthorizedInsts.length === 1) {
                setState(prev => ({ ...prev, inst: mergedAuthorizedInsts[0] }));
            }
        }
    }, [mergedAuthorizedInsts]);

    useEffect(() => {
        if (state.inst && mergedAuthorizedUnits && mergedAuthorizedUnits.length > 0) {
            const filteredUnits = mergedAuthorizedUnits.filter(unit =>
                unit.inst === state.inst
            );
            setDataUnitOptions(filteredUnits);

            if (filteredUnits.length === 1) {
                setState(prev => ({ ...prev, unitId: filteredUnits[0].id }));
                setFixedUnit(filteredUnits[0].id);
            } else if (filteredUnits.length > 1) {
                setFixedUnit(null);
            } else {
                setState(prev => ({ ...prev, unitId: null }));
                setFixedUnit(null);
            }
        } else if (!state.inst) {
            setDataUnitOptions([]);
            setState(prev => ({ ...prev, unitId: null }));
            setFixedUnit(null);
        }
    }, [state.inst, mergedAuthorizedUnits]);

    useEffect(() => {
        setDataDepartmentOptions(config.departments.map(item => item.name));
    }, []);

    // Listele
    const handleResultList = async () => {
        const newStartDate = state.startedDate ? getDateStringYYYYMMDD(state.startedDate, "00:00:01") : null;
        const newFinishedDate = state.finishedDate ? getDateStringYYYYMMDD(state.finishedDate, "23:59:59") : null;

        const filters = {
            inst: state.inst,
            unitId: state.unitId,
            startedDate: newStartDate,
            finishedDate: newFinishedDate,
            department: state.department && state.department.length > 0 ? state.department : null,
            meetingNo: state.meetingNo || null
        };

        setLoading(true);
        try {
            const res = await getDecisionsReportDataApi(filters);
            setData(res.data.data);

            const instLabel = state.inst || '';
            const unit = mergedAuthorizedUnits.find(u => u.id === state.unitId);
            const unitLabel = unit ? unit.name : '';

            setLastFilter({
                ...filters,
                instLabel: instLabel,
                unitLabel: unitLabel
            });
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleGeneratePDF = async () => {
        if (!lastFilter) return;
        setLoading(true);
        try {
            await generateDecisionsReportPDFApi(lastFilter);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleGenerateWORD = async () => {
        if (!lastFilter) return;
        setLoading(true);
        try {
            await generateDecisionsReportWORDApi(lastFilter, "Kararlar_Rapor");
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleGenerateEXCEL = async () => {
        if (!lastFilter) return;
        setLoading(true);
        try {
            await generateDecisionsReportEXCELApi(lastFilter, "Kararlar_Rapor");
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleClear = () => {
        let defaultInst = null;
        let defaultUnitId = null;

        if (mergedAuthorizedInsts && mergedAuthorizedInsts.length === 1) {
            defaultInst = mergedAuthorizedInsts[0];

            if (defaultInst && mergedAuthorizedUnits && mergedAuthorizedUnits.length > 0) {
                const filteredUnits = mergedAuthorizedUnits.filter(unit =>
                    unit.inst === defaultInst
                );

                if (filteredUnits.length === 1) {
                    defaultUnitId = filteredUnits[0].id;
                }
            }
        }

        setState({
            inst: defaultInst,
            unitId: defaultUnitId,
            department: [],
            startedDate: null,
            finishedDate: null,
            meetingNo: ''
        });
        setLastFilter(null);
        setData([]);
    };

    const handleInstChange = (event) => {
        const newInst = event.target.value;
        setState(prev => ({ ...prev, inst: newInst, unitId: null }));
    };

    const handleUnitChange = (event, newValue) => {
        if (newValue) {
            setState(prev => ({ ...prev, unitId: newValue.id }));
        } else {
            setState(prev => ({ ...prev, unitId: null }));
        }
    };

    const handleDepartmentChange = (event, newValue) => {
        setState(prevState => ({ ...prevState, department: newValue }));
    };

    return (
        <div>
            <LoadingPopUp open={loading} />
            <JumboCardQuick title="Kararlar">
                <Grid container spacing={2} mb={2}>
                    <Grid item xs={12} sm={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="inst-label">Kurum *</InputLabel>
                            <Select
                                labelId="inst-label"
                                label="Kurum *"
                                value={state.inst || ''}
                                onChange={handleInstChange}
                                disabled={isInstLocked}
                                required
                            >
                                {dataInstOptions.map((inst) => (
                                    <MenuItem key={inst} value={inst}>
                                        {inst}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={3}>
                        <Autocomplete
                            size="small"
                            id="unit-autocomplete"
                            options={dataUnitOptions}
                            getOptionLabel={(option) => option.name || ''}
                            value={dataUnitOptions.find(u => u.id === state.unitId) || null}
                            onChange={handleUnitChange}
                            disabled={fixedUnit !== null}
                            renderInput={(params) => (
                                <TextField {...params} label="Kurul/Kol/Komisyon *" placeholder="Seçiniz" variant="outlined" size="small" />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12} sm={3}>
                        <Autocomplete
                            multiple
                            size="small"
                            id="department-autocomplete"
                            options={dataDepartmentOptions}
                            value={state.department}
                            onChange={handleDepartmentChange}
                            renderInput={(params) => (
                                <TextField {...params} label="İlgili Konu/Birim" placeholder="Seçiniz" variant="outlined" size="small" />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12} sm={3}>
                        <TextField
                            fullWidth
                            label="Toplantı Sayısı"
                            variant="outlined"
                            size="small"
                            value={state.meetingNo}
                            onChange={(e) =>
                                setState(prevState => ({ ...prevState, meetingNo: e.target.value }))
                            }
                        />
                    </Grid>

                    <Grid item xs={12} sm={3}>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="tr">
                            <DatePicker
                                label="Başlangıç Tarihi"
                                size="small"
                                value={state.startedDate}
                                onChange={(newValue) =>
                                    setState(prevState => ({ ...prevState, startedDate: newValue }))
                                }
                                renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                            />
                        </LocalizationProvider>
                    </Grid>

                    <Grid item xs={12} sm={3}>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="tr">
                            <DatePicker
                                label="Bitiş Tarihi"
                                size="small"
                                value={state.finishedDate}
                                onChange={(newValue) =>
                                    setState(prevState => ({ ...prevState, finishedDate: newValue }))
                                }
                                renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                            />
                        </LocalizationProvider>
                    </Grid>

                    <Grid item xs={12}>
                        <ButtonGroup>
                            <Button
                                variant="outlined"
                                size="medium"
                                color="success"
                                startIcon={<List />}
                                onClick={handleResultList}
                                disabled={!isFormValid()}
                            >
                                Listele
                            </Button>
                            <Button
                                variant="outlined"
                                size="medium"
                                color="success"
                                startIcon={<PictureAsPdf />}
                                disabled={!lastFilter || data.length === 0}
                                onClick={handleGeneratePDF}
                            >
                                PDF
                            </Button>
                            <Button
                                variant="outlined"
                                size="medium"
                                color="primary"
                                startIcon={<DescriptionIcon />}
                                disabled={!lastFilter || data.length === 0}
                                onClick={handleGenerateWORD}
                            >
                                Word
                            </Button>
                            <Button
                                variant="outlined"
                                size="medium"
                                color="warning"
                                startIcon={<TableChart />}
                                disabled={!lastFilter || data.length === 0}
                                onClick={handleGenerateEXCEL}
                            >
                                EXCEL
                            </Button>
                            <Button
                                variant="outlined"
                                size="medium"
                                color="error"
                                startIcon={<DeleteIcon />}
                                onClick={handleClear}
                            >
                                TEMİZLE
                            </Button>
                        </ButtonGroup>
                    </Grid>
                </Grid>
            </JumboCardQuick>

            <DecisionsReportTable data={data} loading={loading} lastFilter={lastFilter} />
        </div>
    );
}

export default ReportDecisionsReportPage;
